import { useMemo } from 'react'
import styles from './RectangleHorizontalTemplate.module.scss'
import commonStyles from '../../TableCardsManagement.module.scss'
import TableCardQrCode from '../../components/TableCardQrCode/TableCardQrCode'
import Przelewy24Icon from '../assets/icons/przelewy24.svg?react'
import VisaIcon from '../assets/icons/visa.svg?react'
import MastercardIcon from '../assets/icons/masterCard.svg?react'
import ApplePayIcon from '../assets/icons/applePay.svg?react'
import GooglePayIcon from '../assets/icons/googlePay.svg?react'
import PaypalIcon from '../assets/icons/payPal.svg?react'
import BlikIcon from '../assets/icons/blik.svg?react'
import NfcIcon from '../assets/icons/nfc.svg?react'

interface RectangleHorizontalTemplateProps {
  withNfc?: boolean
  tableNumber: number
  qrCodeValue?: string
  languageCode?: 'en' | 'pl'
  logoImageUrl: string
  backgroundColorValue?: string
  contentColor?: string
  contrastColor?: string
  backgroundImage?: string | null
  width: number
  height: number
}

const BASE_WIDTH = 681
const BASE_HEIGHT = 468
const BASE_ASPECT_RATIO = BASE_WIDTH / BASE_HEIGHT
const ASPECT_RATIO_TOLERANCE = 0.1 // 10%

function RectangleHorizontalTemplate(props: RectangleHorizontalTemplateProps) {
  const {
    withNfc,
    tableNumber,
    qrCodeValue,
    languageCode = 'en',
    logoImageUrl,
    backgroundColorValue,
    contentColor,
    contrastColor,
    backgroundImage,
    width,
    height
  } = props

  const {
    templateScale,
    offsetLeft,
    offsetTop,
    isAspectRatioValid,
    aspectRatioDeviationPercent
  } = useMemo(() => {
    const fitScale = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT) || 0
    const computedOffsetLeft = (width - BASE_WIDTH * fitScale) / 2 / fitScale
    const computedOffsetTop = (height - BASE_HEIGHT * fitScale) / 2 / fitScale

    const userAspectRatio =
      width > 0 && height > 0 ? width / height : BASE_ASPECT_RATIO
    const relativeDeviation =
      Math.abs(userAspectRatio - BASE_ASPECT_RATIO) / BASE_ASPECT_RATIO
    const valid = relativeDeviation <= ASPECT_RATIO_TOLERANCE

    return {
      templateScale: fitScale,
      offsetLeft: computedOffsetLeft,
      offsetTop: computedOffsetTop,
      isAspectRatioValid: valid,
      aspectRatioDeviationPercent: relativeDeviation * 100
    }
  }, [width, height])

  return (
    <div
      className={styles.rectangleHorizontalTemplateWrapper}
      style={{
        position: 'relative',
        backgroundColor: backgroundColorValue,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: `${width}px`,
        height: `${height}px`
      }}
      data-has-aspect-ratio-warning={isAspectRatioValid ? undefined : 'true'}
      title={
        isAspectRatioValid
          ? undefined
          : `Size deviates ${aspectRatioDeviationPercent.toFixed(
              1
            )}% from 681:468`
      }
      aria-invalid={isAspectRatioValid ? undefined : true}
    >
      <div
        className={commonStyles.contrastOverlay}
        style={{ backgroundColor: contrastColor }}
      />
      <div
        className={styles.rectangleContentWrapper}
        style={{
          position: 'absolute',
          left: `${offsetLeft}px`,
          top: `${offsetTop}px`,
          width: `${BASE_WIDTH}px`,
          height: `${BASE_HEIGHT}px`,
          zoom: templateScale
        }}
      >
        <div className={styles.templateContent}>
          <div className={styles.templateLogoAndDescriptionContainer}>
            <p
              className={`${styles.templateDescription} display-medium`}
              style={{ color: contentColor }}
            >
              {languageCode === 'pl'
                ? 'zobacz menu i zapłać rachunek'
                : 'check the menu & pay the bill'}
            </p>
            <div className={styles.logoAndNfcContainer}>
              <img
                className={styles.logoImage}
                src={logoImageUrl}
                alt="Logo"
                style={{
                  borderColor: contentColor,
                  color: contentColor
                }}
              />
              {withNfc && (
                <NfcIcon
                  className={styles.nfcIcon}
                  style={{ color: contentColor }}
                />
              )}
            </div>
          </div>
          <div className={styles.qrCodeAndTableNumberContainer}>
            <div
              className={styles.qrCodeImageWrapper}
              style={{ backgroundColor: contentColor }}
            >
              <TableCardQrCode
                className={styles.qrCodeImage}
                value={qrCodeValue}
                style={{ borderColor: contentColor }}
              />
            </div>
            <div className={styles.templateMenuAndTableNumberWrapper}>
              <p
                className={`${styles.tableNumber} display-medium`}
                style={{ color: contentColor }}
              >
                {tableNumber}
              </p>
              <p
                className={`${styles.templateMenuText} logo-font-template`}
                style={{ color: contentColor }}
              >
                MENU!
              </p>
            </div>
          </div>
        </div>
        <div
          className={styles.templateSeparator}
          style={{ backgroundColor: contentColor }}
        />
        <div className={styles.templateFooter}>
          <div className={styles.paymentInfoContainer}>
            <div
              className={styles.paymentInfoItem}
              style={{ color: contentColor }}
            >
              <i
                className={`icon-big las la-globe-europe ${styles.infoIcon}`}
              />
              <p className="label-large">
                {languageCode === 'pl' ? 'płatności online' : 'online payment'}
              </p>
            </div>
            <div
              className={styles.paymentInfoItem}
              style={{ color: contentColor }}
            >
              <i className={`icon-big las la-lock ${styles.infoIcon}`} />
              <p className="label-large">
                {languageCode === 'pl'
                  ? 'całkowicie bezpiecznie'
                  : '100% secure'}
              </p>
            </div>
          </div>
          <div className={styles.paymentMethodsIcons}>
            <Przelewy24Icon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <BlikIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <VisaIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <MastercardIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <ApplePayIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <GooglePayIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
            <PaypalIcon
              className={styles.paymentMethodIcon}
              style={{ color: contentColor }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RectangleHorizontalTemplate
